import { useUserContext } from "context/UserContext"
import { FeatureFlags } from "types/FeatureFlags"
import { useFeatureFlagEnabled } from "posthog-js/react"
// @ts-ignore - PostHog React types may not be available

/**
 * Hook to determine if verification is required and if user is verified.
 * Respects PostHog feature flags for email and mobile verification.
 *
 * Feature flags in PostHog (default should be enabled/true):
 * - frontend-email-verification-required-{env}
 * - frontend-mobile-verification-required-{env}
 *
 * When both flags are disabled, verification is never required.
 * When only email flag is enabled, only email verification is checked.
 * When only mobile flag is enabled, only mobile verification is checked.
 * When both flags are enabled, either email OR mobile verification satisfies the requirement.
 */
export function useVerificationRequired() {
    const { user } = useUserContext()

    // Use PostHog feature flags - default to true if flag is not set (undefined)
    const emailFlagEnabled = useFeatureFlagEnabled(
        FeatureFlags.EMAIL_VERIFICATION_REQUIRED
    )
    const mobileFlagEnabled = useFeatureFlagEnabled(
        FeatureFlags.MOBILE_VERIFICATION_REQUIRED
    )

    // console.log("emailFlagEnabled", emailFlagEnabled)
    // console.log("mobileFlagEnabled", mobileFlagEnabled)

    // PostHog returns undefined while loading, null if not found, or boolean
    // Default to true (verification required) if flag is not explicitly set to false
    const isEmailVerificationRequired = emailFlagEnabled !== false
    const isMobileVerificationRequired = mobileFlagEnabled !== false

    const isEmailVerified = user?.verification?.is_email_verified ?? false
    const isPhoneVerified = user?.verification?.is_phone_verified ?? false

    // If no verification is required by feature flags, user is considered "verified"
    if (!isEmailVerificationRequired && !isMobileVerificationRequired) {
        return {
            isVerificationRequired: false,
            isUserVerified: true,
            isUserUnverified: false,
            isEmailVerificationRequired,
            isMobileVerificationRequired,
            isEmailVerified,
            isPhoneVerified,
        }
    }

    // Check if user meets verification requirements based on enabled flags
    let meetsRequirements = false

    if (isEmailVerificationRequired && isMobileVerificationRequired) {
        // Both flags enabled: either email OR phone verification satisfies
        meetsRequirements = isEmailVerified || isPhoneVerified
    } else if (isEmailVerificationRequired) {
        // Only email flag enabled: must have email verified
        meetsRequirements = isEmailVerified
    } else if (isMobileVerificationRequired) {
        // Only mobile flag enabled: must have phone verified
        meetsRequirements = isPhoneVerified
    }

    return {
        isVerificationRequired: true,
        isUserVerified: meetsRequirements,
        isUserUnverified: !meetsRequirements,
        isEmailVerificationRequired,
        isMobileVerificationRequired,
        isEmailVerified,
        isPhoneVerified,
    }
}

export default useVerificationRequired
